import { useIntl } from '@leagueplatform/locales';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { Masonry } from '@leagueplatform/masonry';
import { StackLayout } from '@leagueplatform/genesis-core';
import { CONTENT_MAX_WIDTH } from '@leagueplatform/web-common-components';
import type { LocalesKey } from 'types/types';

type MasonryPageProps = {
  // Analytics screen name reported via usePageViewAnalytics.
  screenName: string;
  // App locales key used for the document <title>.
  titleMessageId: LocalesKey;
  // Masonry root node id whose server-driven tree this page renders.
  rootNodeId: string;
};

// Shared shell for the full-width, Masonry-driven primary pages (Home,
// Support): page-view analytics + document title, then a single content
// column centered and capped at CONTENT_MAX_WIDTH that mounts the Masonry tree.
// Centralizing it keeps the layout consistent in one place rather than
// re-inlined per page.
export const MasonryPage = ({
  screenName,
  titleMessageId,
  rootNodeId,
}: MasonryPageProps) => {
  usePageViewAnalytics({ screen_name: screenName });
  const { formatMessage } = useIntl();
  useDocumentTitle(formatMessage({ id: titleMessageId }));

  return (
    <StackLayout
      orientation="vertical"
      verticalAlignment="center"
      horizontalAlignment="stretch"
      css={{
        marginX: 'auto',
        padding: '$none $two',
        maxWidth: CONTENT_MAX_WIDTH,
        '@mobile': {
          padding: '$none $one',
        },
      }}
    >
      <Masonry rootNodeId={rootNodeId} />
    </StackLayout>
  );
};
